import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  // Reset Password states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordNewPassword, setResetPasswordNewPassword] = useState('');
  const [resetPasswordConfirmPassword, setResetPasswordConfirmPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetPasswordStep, setResetPasswordStep] = useState<'email' | 'password' | 'confirm'>('email');

  // First Login Password Change states
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [firstLoginEmail, setFirstLoginEmail] = useState('');
  const [firstLoginCurrentPassword, setFirstLoginCurrentPassword] = useState('');
  const [firstLoginNewPassword, setFirstLoginNewPassword] = useState('');
  const [firstLoginConfirmPassword, setFirstLoginConfirmPassword] = useState('');
  const [firstLoginError, setFirstLoginError] = useState('');
  const [firstLoginSuccess, setFirstLoginSuccess] = useState(false);
  const [showFirstLoginNewPassword, setShowFirstLoginNewPassword] = useState(false);
  const [showFirstLoginConfirmPassword, setShowFirstLoginConfirmPassword] = useState(false);
  const [firstLoginIsLoading, setFirstLoginIsLoading] = useState(false);
  const [isPasswordExpired, setIsPasswordExpired] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLocked(false);
    setIsLoading(true);

    // Store email temporarily for error handling, then clear form immediately
    const attemptedEmail = email;
    const attemptedPassword = password;

    // Clear form fields immediately after submit to prevent prepopulation
    setEmail('');
    setPassword('');

    try {
      const success = await login(attemptedEmail, attemptedPassword);
      if (!success) {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err: any) {
      // Check if user must change password on first login
      if (err?.mustChangePassword) {
        setFirstLoginEmail(attemptedEmail);
        setFirstLoginCurrentPassword(attemptedPassword);
        setShowFirstLoginModal(true);
        return;
      }

      // Check if the error response contains account locked or password expired information
      // Handle both axios errors and errors with response data
      const errorData = err?.response?.data || err?.data;

      if (errorData) {
        console.log('Error data found:', errorData);
        if (errorData.error === 'ACCOUNT_LOCKED') {
          setIsLocked(true);
          setError(errorData.message);
        } else if (errorData.error === 'PASSWORD_EXPIRED') {
          // Password has expired - show the password change modal
          setIsPasswordExpired(true);
          setFirstLoginEmail(attemptedEmail);
          setFirstLoginCurrentPassword(attemptedPassword);
          setShowFirstLoginModal(true);
          return;
        } else if (errorData.message) {
          setError(errorData.message);
        } else if (errorData.error) {
          setError(errorData.error);
        } else {
          setError('Invalid email or password. Please try again.');
        }
      } else if (err?.message && err.message !== 'MUST_CHANGE_PASSWORD') {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordVerifyEmail = async () => {
    if (!resetPasswordEmail.trim()) {
      setResetPasswordError('Please enter an email address');
      return;
    }

    try {
      const users = await apiService.getUsers();
      const user = users.find((u: any) => u.email.toLowerCase() === resetPasswordEmail.trim().toLowerCase());

      if (!user) {
        setResetPasswordError('No user found with this email address');
        return;
      }

      setResetPasswordError('');
      setResetPasswordStep('password');
    } catch (error) {
      setResetPasswordError('Failed to verify email. Please try again.');
    }
  };

  const handleResetPasswordNext = () => {
    if (!resetPasswordNewPassword.trim()) {
      setResetPasswordError('Please enter a new password');
      return;
    }
    setResetPasswordError('');
    setResetPasswordConfirmPassword('');
    setResetPasswordStep('confirm');
  };

  const handleResetPasswordBack = () => {
    if (resetPasswordStep === 'confirm') {
      setResetPasswordStep('password');
      setResetPasswordConfirmPassword('');
    } else if (resetPasswordStep === 'password') {
      setResetPasswordStep('email');
      setResetPasswordNewPassword('');
    }
    setResetPasswordError('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordNewPassword.trim()) {
      setResetPasswordError('Please enter a new password');
      return;
    }

    if (!resetPasswordConfirmPassword.trim()) {
      setResetPasswordError('Please confirm the new password');
      return;
    }

    if (resetPasswordNewPassword !== resetPasswordConfirmPassword) {
      setResetPasswordError('Passwords do not match. Please try again.');
      return;
    }

    if (resetPasswordNewPassword.length < 6) {
      setResetPasswordError('Password must be at least 6 characters long');
      return;
    }

    try {
      const users = await apiService.getUsers();
      const user = users.find((u: any) => u.email.toLowerCase() === resetPasswordEmail.trim().toLowerCase());

      if (!user) {
        setResetPasswordError('User not found');
        return;
      }

      await apiService.updateUser(user.id, {
        password: resetPasswordNewPassword
      });

      setResetPasswordSuccess(true);
      setResetPasswordError('');

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setResetPasswordEmail('');
        setResetPasswordNewPassword('');
        setResetPasswordConfirmPassword('');
        setShowResetPassword(false);
        setShowResetConfirmPassword(false);
        setResetPasswordSuccess(false);
        setShowNewPassword(false);
        setResetPasswordStep('email');
      }, 2000);
    } catch (error) {
      setResetPasswordError('Failed to reset password. Please try again.');
    }
  };

  const handleResetPasswordCancel = () => {
    setShowResetPasswordModal(false);
    setResetPasswordEmail('');
    setResetPasswordNewPassword('');
    setResetPasswordConfirmPassword('');
    setShowResetPassword(false);
    setShowResetConfirmPassword(false);
    setResetPasswordError('');
    setResetPasswordSuccess(false);
    setShowNewPassword(false);
    setResetPasswordStep('email');
  };

  // Password validation function (client-side)
  const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 10) {
      errors.push('At least 10 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('At least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('At least one special character (@, !, %, etc.)');
    }

    return { valid: errors.length === 0, errors };
  };

  // First Login Password Change handlers
  const handleFirstLoginSubmit = async () => {
    setFirstLoginError('');

    if (!firstLoginNewPassword.trim()) {
      setFirstLoginError('Please enter a new password');
      return;
    }

    // Validate password requirements
    const validation = validatePassword(firstLoginNewPassword);
    if (!validation.valid) {
      setFirstLoginError('Password must have: ' + validation.errors.join(', '));
      return;
    }

    if (firstLoginNewPassword !== firstLoginConfirmPassword) {
      setFirstLoginError('Passwords do not match');
      return;
    }

    if (firstLoginNewPassword === firstLoginCurrentPassword) {
      setFirstLoginError('New password must be different from your current password');
      return;
    }

    setFirstLoginIsLoading(true);

    try {
      // Change password using the API
      await apiService.changePasswordFirstLogin(
        firstLoginEmail,
        firstLoginCurrentPassword,
        firstLoginNewPassword
      );

      setFirstLoginSuccess(true);

      // After successful password change, automatically log in with new password
      setTimeout(async () => {
        setShowFirstLoginModal(false);
        // Reset all first login states
        setFirstLoginNewPassword('');
        setFirstLoginConfirmPassword('');
        setFirstLoginError('');
        setFirstLoginSuccess(false);
        setShowFirstLoginNewPassword(false);
        setShowFirstLoginConfirmPassword(false);
        setFirstLoginIsLoading(false);

        // Attempt login with new password
        try {
          const success = await login(firstLoginEmail, firstLoginNewPassword);
          if (!success) {
            setError('Password changed but login failed. Please try again.');
          }
        } catch (loginErr: any) {
          setError('Password changed. Please sign in with your new password.');
        }

        // Clear stored credentials
        setFirstLoginEmail('');
        setFirstLoginCurrentPassword('');
      }, 1500);
    } catch (error: any) {
      setFirstLoginIsLoading(false);
      // Handle server validation errors with details array
      const errorData = error?.response?.data;
      if (errorData?.details && Array.isArray(errorData.details)) {
        setFirstLoginError(errorData.details.join('. '));
      } else {
        const errorMessage = errorData?.error || error?.message || 'Failed to change password. Please try again.';
        setFirstLoginError(errorMessage);
      }
    }
  };

  const handleFirstLoginCancel = () => {
    setShowFirstLoginModal(false);
    setFirstLoginEmail('');
    setFirstLoginCurrentPassword('');
    setFirstLoginNewPassword('');
    setFirstLoginConfirmPassword('');
    setFirstLoginError('');
    setFirstLoginSuccess(false);
    setShowFirstLoginNewPassword(false);
    setShowFirstLoginConfirmPassword(false);
    setFirstLoginIsLoading(false);
    setIsPasswordExpired(false);
  };

  return (
    <div className="login-container">
      <div className="login-card md-card md-card--elevated">
        <div className="login-header">
          <h1 className="login-title">APR Framework</h1>
          <p className="login-subtitle">Application Portfolio Rationalization</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {/* Only show email/password fields when account is not locked */}
          {!isLocked && (
            <>
              <div className="md-text-field">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="md-text-field__input"
                  placeholder=" "
                />
                <label htmlFor="email" className="md-text-field__label">
                  Email Address
                </label>
                <div className="md-text-field__outline">
                  <div className="md-text-field__leading"></div>
                  <div className="md-text-field__notch">
                    <div className="md-text-field__label-text">Email Address</div>
                  </div>
                  <div className="md-text-field__trailing"></div>
                </div>
              </div>

              <div className="md-text-field">
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="md-text-field__input"
                  placeholder=" "
                />
                <label htmlFor="password" className="md-text-field__label">
                  Password
                </label>
                <div className="md-text-field__outline">
                  <div className="md-text-field__leading"></div>
                  <div className="md-text-field__notch">
                    <div className="md-text-field__label-text">Password</div>
                  </div>
                  <div className="md-text-field__trailing"></div>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className={`login-error ${isLocked ? 'login-error--locked' : ''}`} style={isLocked ? {
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            } : undefined}>
              <span className="login-error__icon">{isLocked ? '🔒' : '⚠️'}</span>
              <span className="login-error__message" style={isLocked ? { display: 'block', marginTop: '8px', lineHeight: '1.5' } : undefined}>{error}</span>
            </div>
          )}

          {/* Only show Sign In button and Forgot Password when account is not locked */}
          {!isLocked && (
            <>
              <button
                type="submit"
                className="md-button md-button--filled login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>

              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => setShowResetPasswordModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1976d2',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    padding: '8px'
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      {/* Reset Password Modal - 3 Step Wizard */}
      {showResetPasswordModal && (
        <div className="modal-overlay" onClick={handleResetPasswordCancel} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Reset Password
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {resetPasswordStep === 'email' && 'Enter your email address to reset your password'}
                {resetPasswordStep === 'password' && 'Enter your new password'}
                {resetPasswordStep === 'confirm' && 'Confirm your new password'}
              </p>
            </div>

            {/* Step indicator */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px',
              gap: '8px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: resetPasswordStep === 'email' ? '#f59e0b' : '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                {resetPasswordStep === 'email' ? '1' : '✓'}
              </div>
              <div style={{
                width: '40px',
                height: '2px',
                backgroundColor: resetPasswordStep === 'email' ? '#e0e0e0' : '#10b981'
              }}></div>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: resetPasswordStep === 'password' ? '#f59e0b' : resetPasswordStep === 'confirm' ? '#10b981' : '#e0e0e0',
                color: resetPasswordStep === 'email' ? '#757575' : 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                {resetPasswordStep === 'confirm' ? '✓' : '2'}
              </div>
              <div style={{
                width: '40px',
                height: '2px',
                backgroundColor: resetPasswordStep === 'confirm' ? '#f59e0b' : '#e0e0e0'
              }}></div>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: resetPasswordStep === 'confirm' ? '#f59e0b' : '#e0e0e0',
                color: resetPasswordStep === 'confirm' ? 'white' : '#757575',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                3
              </div>
            </div>

            {/* Step 1: Email */}
            {resetPasswordStep === 'email' && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Step 1: Enter Your Email
                </label>
                <input
                  type="email"
                  value={resetPasswordEmail}
                  onChange={(e) => {
                    setResetPasswordEmail(e.target.value);
                    setResetPasswordError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your email"
                  autoFocus
                />
              </div>
            )}

            {/* Step 2: Enter New Password */}
            {resetPasswordStep === 'password' && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#0369a1'
                }}>
                  Resetting password for: <strong>{resetPasswordEmail}</strong>
                </div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Step 2: Enter New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={resetPasswordNewPassword}
                    onChange={(e) => {
                      setResetPasswordNewPassword(e.target.value);
                      setResetPasswordError('');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '40px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter new password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm Password */}
            {resetPasswordStep === 'confirm' && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: '#0369a1'
                }}>
                  Resetting password for: <strong>{resetPasswordEmail}</strong>
                </div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Step 3: Confirm Password
                </label>
                <p style={{fontSize: '14px', color: '#666', margin: '0 0 12px 0'}}>
                  Please re-type the password to confirm it matches exactly.
                </p>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showResetConfirmPassword ? 'text' : 'password'}
                    value={resetPasswordConfirmPassword}
                    onChange={(e) => {
                      setResetPasswordConfirmPassword(e.target.value);
                      setResetPasswordError('');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '40px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Re-type the password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    {showResetConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            )}

            {resetPasswordError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {resetPasswordError}
              </div>
            )}

            {resetPasswordSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                Password reset successfully!
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: '24px'
            }}>
              <div>
                {resetPasswordStep !== 'email' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordBack}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#666',
                      backgroundColor: 'transparent',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    ← Back
                  </button>
                )}
              </div>
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  type="button"
                  onClick={handleResetPasswordCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {resetPasswordStep === 'email' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordVerifyEmail}
                    disabled={!resetPasswordEmail.trim()}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: !resetPasswordEmail.trim() ? '#9ca3af' : '#f59e0b',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: !resetPasswordEmail.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                )}
                {resetPasswordStep === 'password' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordNext}
                    disabled={!resetPasswordNewPassword.trim()}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: !resetPasswordNewPassword.trim() ? '#9ca3af' : '#f59e0b',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: !resetPasswordNewPassword.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                )}
                {resetPasswordStep === 'confirm' && (
                  <button
                    type="button"
                    onClick={handleResetPasswordSubmit}
                    disabled={!resetPasswordConfirmPassword.trim() || resetPasswordSuccess}
                    style={{
                      padding: '10px 24px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'white',
                      backgroundColor: (!resetPasswordConfirmPassword.trim() || resetPasswordSuccess) ? '#9ca3af' : '#10b981',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (!resetPasswordConfirmPassword.trim() || resetPasswordSuccess) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Reset Password
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* First Login Password Change Modal */}
      {showFirstLoginModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: isPasswordExpired ? '#d32f2f' : '#1a1a1a'
              }}>
                {isPasswordExpired ? 'Password Expired' : 'Change Your Password'}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {isPasswordExpired
                  ? 'Your password has expired (passwords must be changed every 90 days). Please create a new password to continue.'
                  : 'This is your first login. For security, please create a new password.'
                }
              </p>
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                <strong style={{ display: 'block', marginBottom: '8px', color: '#333' }}>Password Requirements:</strong>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', lineHeight: '1.8' }}>
                  <li>At least 10 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character (@, !, %, etc.)</li>
                  <li>Cannot reuse last 3 passwords</li>
                </ul>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showFirstLoginNewPassword ? 'text' : 'password'}
                  value={firstLoginNewPassword}
                  onChange={(e) => setFirstLoginNewPassword(e.target.value)}
                  disabled={firstLoginSuccess || firstLoginIsLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    paddingRight: '40px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: (firstLoginSuccess || firstLoginIsLoading) ? '#f5f5f5' : 'white'
                  }}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowFirstLoginNewPassword(!showFirstLoginNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px'
                  }}
                >
                  {showFirstLoginNewPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showFirstLoginConfirmPassword ? 'text' : 'password'}
                  value={firstLoginConfirmPassword}
                  onChange={(e) => setFirstLoginConfirmPassword(e.target.value)}
                  disabled={firstLoginSuccess || firstLoginIsLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    paddingRight: '40px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    backgroundColor: (firstLoginSuccess || firstLoginIsLoading) ? '#f5f5f5' : 'white'
                  }}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowFirstLoginConfirmPassword(!showFirstLoginConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '4px'
                  }}
                >
                  {showFirstLoginConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {firstLoginError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {firstLoginError}
              </div>
            )}

            {firstLoginSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                Password changed successfully! Signing you in...
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                type="button"
                onClick={handleFirstLoginCancel}
                disabled={firstLoginIsLoading || firstLoginSuccess}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: (firstLoginIsLoading || firstLoginSuccess) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: (firstLoginIsLoading || firstLoginSuccess) ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFirstLoginSubmit}
                disabled={firstLoginSuccess || firstLoginIsLoading}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: (firstLoginSuccess || firstLoginIsLoading) ? '#ccc' : '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (firstLoginSuccess || firstLoginIsLoading) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                {firstLoginIsLoading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
